/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { createPortal } from "react-dom";
import { set_value_layer } from "../../App/actions/layerActions";

function A_HTML_CANVAS({
  layer,
  center_x, // Calculated Pixel Coordinates from Parent
  center_y,
  width,
  height,
  title,
  desc,
  // Removed props: font_size_title, font_size_desc (Calculated internally now)
  image_1,
  image_2,
  set_value_layer,
}) {
  const [portalContainer, setPortalContainer] = useState(null);
  const map_object_ref = useRef(layer?.map_object || null);

  // --- INTERNAL STYLING RATIOS ---
  const PADDING_RATIO = 0.05; // 5% of width
  const BORDER_RADIUS_RATIO = 0.07; // 7% of width
  const TITLE_FONT_RATIO = 0.08; // Title is 8% of container width
  const DESC_FONT_RATIO = 0.055; // Description is 5.5% of container width
  // -------------------------------

  useEffect(() => {
    map_object_ref.current = layer?.map_object || null;
    const map = map_object_ref.current;
    if (map) {
      setPortalContainer(map.getCanvasContainer());
    }
  }, [layer?.map_object]);

  const handle_image_click = (e) => {
    e.stopPropagation();
    set_value_layer({
      key: "modal_annotation_data",
      value: { title, desc, image_1, image_2 },
    });
  };

  if (!portalContainer || width <= 0) return null;

  // --- POSITIONING ---
  const left = center_x - width / 2;
  const top = center_y - height / 2;

  // --- DYNAMIC SCALING CALCULATIONS ---
  const scaled_padding = width * PADDING_RATIO;
  const scaled_border_radius = width * BORDER_RADIUS_RATIO;

  // Font Sizes (Pixel based)
  // We use Math.max to ensure text remains legible even if the box is zoomed out very far (min 8px)
  const calc_title_size = width * TITLE_FONT_RATIO;
  const calc_desc_size = width * DESC_FONT_RATIO;

  // NOTE: If you want text to disappear completely when small, remove Math.max
  // Currently set to follow the box size strictly to match "Fixed Image" feel
  const final_title_size = calc_title_size;
  const final_desc_size = calc_desc_size;

  // Image Sizing
  // We constrain image to max 50% of the box HEIGHT to ensure text always has room
  const innerWidth = width - scaled_padding * 2;
  const idealImageHeight = innerWidth * 0.55; // 16:9ish ratio relative to width
  const maxImageHeight = height * 0.5; // Cap at 50% of total height

  const finalImageHeight = Math.min(idealImageHeight, maxImageHeight);

  // --- STYLES ---
  const containerStyle = {
    position: "absolute",
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    pointerEvents: "auto",
    zIndex: 2,
    borderRadius: `${scaled_border_radius}px`,
    overflow: "hidden",
    backgroundColor: "white",
    boxShadow: `0 ${width * 0.02}px ${width * 0.05}px rgba(0,0,0,0.25)`, // Shadow scales with box
    display: "block",
    transform: "none",
    // Fix for strict sizing
    boxSizing: "border-box",
  };

  const contentStyle = {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    padding: `${scaled_padding}px`,
  };

  const imageStyle = {
    width: "100%",
    height: `${finalImageHeight}px`,
    backgroundImage: `url(${image_1})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: `${scaled_border_radius * 0.5}px`,
    flexShrink: 0,
    marginBottom: `${scaled_padding}px`,
    display: image_1 ? "block" : "none",
    cursor: "pointer",
    touchAction: "none",
  };

  const headerStyle = {
    margin: "0",
    fontSize: `${final_title_size}px`,
    fontWeight: 700,
    lineHeight: "1.2",
    color: "#000",
    flexShrink: 0,
    paddingBottom: `${scaled_padding * 0.5}px`,
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const scrollableStyle = {
    flex: 1,
    overflowY: "hidden", // Hidden by default to keep "Fixed Image" look, 'auto' if you want scrolling
    minHeight: 0,
    wordWrap: "break-word",
  };

  const pStyle = {
    margin: "0",
    fontSize: `${final_desc_size}px`,
    lineHeight: "1.4",
    color: "#444",
  };

  return createPortal(
    <div style={containerStyle}>
      <section style={contentStyle}>
        {image_1 && (
          <div
            style={imageStyle}
            onClick={handle_image_click}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handle_image_click(e);
            }}
            onTouchStart={(e) => e.stopPropagation()}
            role="button"
          />
        )}

        {<h1 style={headerStyle}>{title}</h1>}

        <div style={scrollableStyle}>{<p style={pStyle}>{desc}</p>}</div>
      </section>
    </div>,
    portalContainer
  );
}

const mapStateToProps = (state) => ({ layer: state.layer });
export default connect(mapStateToProps, { set_value_layer })(A_HTML_CANVAS);
